import { GraphQLClient } from "graphql-request";
import { createAuthAwareFetch } from "@noiman/shared-auth/auth-fetch";

export interface GraphqlRuntimeConfig {
  apiUrl?: string;
  graphqlEndpoint?: string;
}

export interface GraphqlRequestConfig {
  endpoint?: string;
  runtime?: GraphqlRuntimeConfig;
  request?: Request;
  headers?: HeadersInit;
  fetchImpl?: typeof fetch;
}

const PRODUCTION_GRAPHQL_ENDPOINT = "https://api-vps.yumesaku.tech/graphql";
const LOCAL_GRAPHQL_ENDPOINT = "http://localhost:8090/graphql";
const BROWSER_GRAPHQL_PATH = "/graphql";
const REQUEST_CORRELATION_HEADERS = ["X-Request-ID", "CF-Ray"] as const;
const SAFE_REQUEST_CORRELATION_ID = /^[A-Za-z0-9._:-]{8,80}$/;

type RequestCorrelationHeader = (typeof REQUEST_CORRELATION_HEADERS)[number];

interface RequestCorrelation {
  header: RequestCorrelationHeader;
  value: string;
}

function getDefaultEndpoint(): string {
  // Browser requests must stay on the page origin. The Client Worker already
  // proxies /graphql to api-go, so this keeps host-only session cookies intact
  // and avoids a cross-origin preflight to the Cloud Run URL.
  // graphql-request requires an absolute URL, hence URL() rather than a bare
  // "/graphql" path.
  if (typeof window !== "undefined") {
    return new URL(BROWSER_GRAPHQL_PATH, window.location.origin).toString();
  }
  // Check for Node.js production environment
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "production") {
    return PRODUCTION_GRAPHQL_ENDPOINT;
  }
  return LOCAL_GRAPHQL_ENDPOINT;
}

export function resolveGraphqlEndpoint(
  runtime?: GraphqlRuntimeConfig
): string {
  if (runtime?.graphqlEndpoint) {
    return runtime.graphqlEndpoint;
  }

  if (runtime?.apiUrl) {
    return buildEndpoint(runtime.apiUrl);
  }

  // Never let a browser-side process shim choose a server endpoint. Explicit
  // runtime overrides above still remain available for intentional callers.
  if (typeof window !== "undefined") {
    return getDefaultEndpoint();
  }

  if (typeof process !== "undefined") {
    if (process.env.NOIMAN_GRAPHQL_ENDPOINT) {
      return process.env.NOIMAN_GRAPHQL_ENDPOINT;
    }

    if (process.env.API_URL) {
      return buildEndpoint(process.env.API_URL);
    }
  }

  return getDefaultEndpoint();
}

function buildEndpoint(baseUrl: string) {
  return `${baseUrl.replace(/\/$/, "")}/graphql`;
}

function getRequestCorrelation(request: Request): RequestCorrelation | null {
  for (const header of REQUEST_CORRELATION_HEADERS) {
    const value = request.headers.get(header);
    if (value !== null && SAFE_REQUEST_CORRELATION_ID.test(value)) {
      return { header, value };
    }
  }

  return null;
}

function mergeHeaders(request?: Request, headers?: HeadersInit): HeadersInit {
  const merged = new Headers();
  merged.set("Content-Type", "application/json");

  if (request) {
    const cookie = request.headers.get("cookie");
    if (cookie) {
      merged.set("Cookie", cookie);
    }

    const authorization = request.headers.get("authorization");
    if (authorization && !merged.has("Authorization")) {
      merged.set("Authorization", authorization);
    }
  }

  if (headers) {
    const extra = new Headers(headers);
    extra.forEach((value, key) => {
      merged.set(key, value);
    });
  }

  if (request) {
    // Only an explicitly supplied, log-safe inbound request identifier may be
    // correlated with the Go API. Never derive one from auth or cookie data.
    const correlation = getRequestCorrelation(request);
    for (const header of REQUEST_CORRELATION_HEADERS) {
      merged.delete(header);
    }
    if (correlation) {
      merged.set(correlation.header, correlation.value);
    }
  }

  return merged;
}

export function createGraphqlClient(config?: GraphqlRequestConfig): GraphQLClient {
  const endpoint = config?.endpoint ?? resolveGraphqlEndpoint(config?.runtime);
  const headers = mergeHeaders(config?.request, config?.headers);

  return new GraphQLClient(endpoint, {
    credentials: "include",
    headers,
    fetch: config?.fetchImpl ?? (createAuthAwareFetch(config?.request) as typeof fetch),
  });
}
